import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import QuickAddItemModal from './QuickAddItemModal';
import { NotesService } from '../../services/notes.service';
import { CardDetailsService } from '../../services/cardDetails.service';
import { BankDetailsService } from '../../services/bankDetails.service';
import { PersonalDetailsService } from '../../services/personalDetails.service';
import { VaultService } from '../../services/vault.service';
import { Note } from '../../types/notes';
import { CardDetail } from '../../types/cardDetails';
import { BankDetail } from '../../types/bankDetails';
import { PersonalDetail } from '../../types/personalDetails';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import { PlusIcon } from '@heroicons/react/24/outline';
import AddItemModal from './AddItemModal';
import VaultItemActionModal from './VaultItemActionModal';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import PasswordModal from '../vault/PasswordModal';
import CardDetailsModal from '../cardDetails/CardDetailsModal';
import BankDetailsModal from '../bankDetails/BankDetailsModal';
import PersonalDetailsModal from '../personalDetails/PersonalDetailsModal';
import { secureStore } from '../../utils/secureStore';

interface QuickAddItemProps {
    categoryId: string;
    categoryName: string;
    onUpdate: () => void;
    isShared: boolean;
    sharedKey: string;
    onQuickAddItemSelect?: (categoryId: string, itemType: string) => void;
}

const QuickAddItem: React.FC<QuickAddItemProps> = ({ 
    categoryId, 
    categoryName,
    onUpdate, 
    isShared,
    sharedKey,
    onQuickAddItemSelect 
}) => {
    // Action modal states
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
    const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);

    // Existing item states
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [items, setItems] = useState<any[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const { activeWorkspace } = useWorkspace();

    useEffect(() => {
        if (selectedType) {
            fetchUncategorizedItems(selectedType);
        }
    }, [selectedType]);

    const fetchUncategorizedItems = async (type: string) => {
        try {
            let items: any[] = [];
            switch (type) {
                case 'password':
                    items = await VaultService.getPasswordsNotInThisVault(categoryId);
                    console.log("items", items);
                    break;
                case 'notes':
                    const notes = await NotesService.getAllNotes();
                    items = notes.filter(note => !note.category_id);
                    break;
                case 'cardDetails':
                    items = await CardDetailsService.getCardDetailsNotInThisVault(categoryId);
                    break;
                case 'bankDetails':
                    items = await BankDetailsService.getBankDetailsNotInThisVault(categoryId);
                    break;
                case 'personalDetails':
                    items = await PersonalDetailsService.getPersonalDetailsNotInThisVault(categoryId);
                    break;
            }
            setItems(items);
        } catch (error) {
            console.error('Error fetching unassigned items:', error);
            toast.error('Failed to fetch items');
        }
    };

    const handleItemTypeSelect = (type: string) => {
        setSelectedType(type);
        fetchUncategorizedItems(type);
    };

    const handleItemSelect = async () => {
        if (!selectedItemId || !selectedType) return;

        try {
            const selectedItem = items.find(item => item.id === selectedItemId);
            if (!selectedItem) return;
        

            switch (selectedType) {
                case 'password':
                    await VaultService.updateEntry(selectedItemId, {
                        ...selectedItem,
                        isSharedUpdate: !selectedItem.is_owner,
                        sharedKey: selectedItem.is_owner ? secureStore.getVaultKey() : selectedItem?.shared_key,
                        category_id: categoryId,
                        itemType: sharedKey ? 'category' : 'password',
                    }, activeWorkspace?.id || undefined);
                    break;
                case 'notes':
                    await NotesService.updateNote(selectedItemId, {
                        id: selectedItem.id,
                        name: selectedItem.name,
                        encrypted_notes: selectedItem.encrypted_notes,
                        category_id: categoryId,
                        favourite: selectedItem.favourite
                    });
                    break;
                case 'cardDetails':
                    await CardDetailsService.updateCardDetail(selectedItemId, {
                        ...selectedItem,
                        category_id: categoryId,
                        isSharedUpdate: isShared,
                        shared_key: sharedKey,
                        itemType: sharedKey ? 'category' : 'card',
                    });
                    break;
                case 'bankDetails':
                    await BankDetailsService.updateBankDetail(selectedItemId, {
                        ...selectedItem,
                        category_id: categoryId,
                        isSharedUpdate: isShared,
                        shared_key: sharedKey,
                        itemType: sharedKey ? 'category' : 'bank',
                    });
                    break;
                case 'personalDetails':
                    await PersonalDetailsService.updatePersonalDetail(selectedItemId, {
                        ...selectedItem,
                        category_id: categoryId,
                        isSharedUpdate: isShared,
                        shared_key: sharedKey,
                        itemType: sharedKey ? 'category' : 'personal',
                    });
                    break;
            }

            toast.success('Item added to vault');
            onUpdate();
            setIsQuickAddModalOpen(false);
            setSelectedType(null);
            setSelectedItemId('');
        } catch (error) {
            console.error('Error updating item:', error);
            toast.error('Failed to add item to vault');
        }
    };

    return (
        <>
            <button
                onClick={() => setIsActionModalOpen(true)}
                className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500"
            >
                <PlusIcon className="h-5 w-5" />
            </button>

            <VaultItemActionModal
                isOpen={isActionModalOpen}
                onClose={() => setIsActionModalOpen(false)}
                onCreateNew={() => setIsAddItemModalOpen(true)}
                onAddExisting={() => setIsQuickAddModalOpen(true)}
                vaultName={categoryName}
            />

            <QuickAddItemModal
                isOpen={isQuickAddModalOpen}
                onClose={() => {
                    setIsQuickAddModalOpen(false);
                    setSelectedType(null);
                    setSelectedItemId('');
                }}
                onSelect={handleItemTypeSelect}
                selectedType={selectedType}
                items={items}
                onItemSelect={setSelectedItemId}
                onSubmit={handleItemSelect}
                isShared={isShared}
                sharedKey={sharedKey}
            />

            <AddItemModal
                isOpen={isAddItemModalOpen}
                onClose={() => setIsAddItemModalOpen(false)}
                onSelect={(type) => {
                    setIsAddItemModalOpen(false);
                    if (onQuickAddItemSelect) {
                        onQuickAddItemSelect(categoryId, type);
                    }
                }}
                categoryId={categoryId}
            />
        </>
    );
};

export default QuickAddItem; 